import { CreativeEditorSection } from "./CreativeEditorDialog";
import { Type } from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { THEME_FONTS, THEME_HEADING_FONTS } from "@/lib/theme/theme-presets";
import type { BrandTypographyProps } from "@/types/creative-library";

// Suggestions reuse the app's font catalog; they are not a provider support list.
const fonts = [
  ...new Set(
    [...Object.values(THEME_FONTS), ...Object.values(THEME_HEADING_FONTS)].map(
      (font) => font.label,
    ),
  ),
];

export function BrandTypography({ value, onChange }: BrandTypographyProps) {
  const { t } = useTranslation("app");
  return (
    <CreativeEditorSection>
      <div className="flex items-center gap-2">
        <Type className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <Label htmlFor="brand-typography">
          {t("creativeLibrary.typography")}
        </Label>
      </div>
      <Select
        value=""
        onValueChange={(font) => onChange(value ? `${value}\n${font}` : font)}
      >
        <SelectTrigger
          aria-label={t("creativeLibrary.fontSuggestions")}
          data-testid="creative-brand-font-suggestions"
        >
          <SelectValue placeholder={t("creativeLibrary.fontSuggestions")} />
        </SelectTrigger>
        <SelectContent>
          {fonts.map((font) => (
            <SelectItem
              key={font}
              value={font}
              disabled={value.length + font.length + (value ? 1 : 0) > 1000}
            >
              {font}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Textarea
        id="brand-typography"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="creative-brand-typography"
        rows={2}
        maxLength={1000}
        placeholder={t("creativeLibrary.typographyPlaceholder")}
      />
      <Text size="xs" color="muted">
        {t("creativeLibrary.typographyHelp")}
      </Text>
    </CreativeEditorSection>
  );
}
